import api from "./api";

const getGroups = (search) => {
  return api
    .get("/state_set/data", {
      params: {
        stateSetId: "Group",
        ...search,
      },
    })
    .then((response) => {
      return response.data;
    });
};

// Get root groups (groups that are not children of any other group)
const getRootGroups = (search) => {
  return api
    .get("/group/roots", {
      params: {
        ...search,
      },
    })
    .then((response) => {
      return response.data;
    });
};

const getGroup = (id) => {
  return api
    .get(`/state_set/data/Group/${id}`)
    .then((response) => {
      return response.data;
    });
};

const getGroupUi = () => {
  return api
    .get("/state_set/ui/Group")
    .then((response) => {
      return response.data;
    });
};

const createGroup = (group) => {
  return api
    .post("/state_set/data", {
      stateSetId: "Group",
      ...group,
    })
    .then((response) => {
      clearGroupCache();
      return response.data;
    });
};

const updateGroup = (group) => {
  return api
    .patch("/state_set/data", {
      stateSetId: "Group",
      ...group,
    })
    .then((response) => {
      clearGroupCache();
      return response.data;
    });
};

const deleteGroup = (id) => {
  return api
    .delete(`/state_set/data/Group/${id}`)
    .then((response) => {
      clearGroupCache();
      return response.data;
    });
};

// Cache for contributors, roles, teams, and groups data with 2-minute expiry
let contributorsCache = null;
let contributorsCacheTime = 0;
let groupRolesCache = null;
let groupRolesCacheTime = 0;
let teamsCache = null;
let teamsCacheTime = 0;
let allGroupsCache = null;
let allGroupsCacheTime = 0;
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

const fetchGroupRelatedData = async () => {
  const now = Date.now();

  // Fetch contributors if cache is expired or empty
  if (!contributorsCache || now - contributorsCacheTime > CACHE_DURATION) {
    const contributorsResponse = await api.get("/state_set/data", {
      params: {
        stateSetId: "Contributor",
        page: 0,
        pageSize: 99999,
      },
    });
    contributorsCache = contributorsResponse.data.list || [];
    contributorsCacheTime = now;
  }

  // Fetch group roles if cache is expired or empty
  if (!groupRolesCache || now - groupRolesCacheTime > CACHE_DURATION) {
    const rolesResponse = await api.get("/state_set/data", {
      params: {
        stateSetId: "GroupRoles",
        page: 0,
        pageSize: 99999,
      },
    });
    groupRolesCache = rolesResponse.data.list || [];
    groupRolesCacheTime = now;
  }

  // Fetch teams if cache is expired or empty
  if (!teamsCache || now - teamsCacheTime > CACHE_DURATION) {
    const teamsResponse = await api.get("/state_set/data", {
      params: {
        stateSetId: "Team",
        page: 0,
        pageSize: 99999,
      },
    });
    teamsCache = teamsResponse.data.list || [];
    teamsCacheTime = now;
  }

  // Fetch all groups if cache is expired or empty (for child_group lookup)
  if (!allGroupsCache || now - allGroupsCacheTime > CACHE_DURATION) {
    const groupsResponse = await api.get("/state_set/data", {
      params: {
        stateSetId: "Group",
        page: 0,
        pageSize: 99999,
      },
    });
    allGroupsCache = groupsResponse.data.list || [];
    allGroupsCacheTime = now;
  }

  return {
    contributors: contributorsCache,
    groupRoles: groupRolesCache,
    teams: teamsCache,
    allGroups: allGroupsCache,
  };
};

const getGroupMembers = async (groupId) => {
  try {
    const { contributors, groupRoles } = await fetchGroupRelatedData();

    // Get the group data
    const groupResponse = await api.get(`/state_set/data/Group/${groupId}`);
    const group = groupResponse.data;

    if (!group.group_role || !Array.isArray(group.group_role)) {
      return [];
    }

    // Create a map of contributor ID to contributor details
    // Map by _id, id, and contributor_id since the reference might use any of these
    const contributorMap = {};
    contributors.forEach((contributor) => {
      const details = {
        // Contributor uses 'name' field, not 'full_name'
        fullName: contributor.name,
        email: contributor.email,
      };
      if (contributor._id) {
        contributorMap[contributor._id] = details;
      }
      if (contributor.id && contributor.id !== contributor._id) {
        contributorMap[contributor.id] = details;
      }
      if (contributor.contributor_id) {
        contributorMap[contributor.contributor_id] = details;
      }
    });

    // Create a map of role ID to role details
    // Map both _id and id since the reference might use either
    const roleMap = {};
    groupRoles.forEach((role) => {
      const details = {
        name: role.name,
        description: role.description,
      };
      if (role._id) {
        roleMap[role._id] = details;
      }
      if (role.id && role.id !== role._id) {
        roleMap[role.id] = details;
      }
    });

    const members = [];

    group.group_role.forEach((roleEntry) => {
      const contributorId = roleEntry.contributor;
      const roleId = roleEntry._id;

      const contributorDetails = contributorMap[contributorId] || {
        fullName: "Unknown Contributor",
        email: "",
      };
      const roleDetails = roleMap[roleId] || {
        name: "Unknown Role",
        description: "",
      };

      members.push({
        contributorId,
        contributorName: contributorDetails.fullName,
        contributorEmail: contributorDetails.email,
        roleId,
        roleName: roleDetails.name,
        roleDescription: roleDetails.description,
      });
    });

    return members;
  } catch (error) {
    throw error;
  }
};

// Get all group data for all groups at once (members, teams, child groups)
const getAllGroupData = async (groups) => {
  try {
    const { contributors, groupRoles, teams, allGroups } = await fetchGroupRelatedData();

    // Create a map of contributor ID to contributor details
    // Map by _id, id, and contributor_id since the reference might use any of these
    const contributorMap = {};
    contributors.forEach((contributor) => {
      const details = {
        // Contributor uses 'name' field, not 'full_name'
        fullName: contributor.name,
        email: contributor.email,
      };
      // Map by _id
      if (contributor._id) {
        contributorMap[contributor._id] = details;
      }
      // Also map by id if different
      if (contributor.id && contributor.id !== contributor._id) {
        contributorMap[contributor.id] = details;
      }
      // Also map by contributor_id if present
      if (contributor.contributor_id) {
        contributorMap[contributor.contributor_id] = details;
      }
    });

    // Create a map of role ID to role details
    // Map both _id and id since the reference might use either
    const roleMap = {};
    groupRoles.forEach((role) => {
      const details = {
        name: role.name,
        description: role.description,
      };
      // Map by _id
      if (role._id) {
        roleMap[role._id] = details;
      }
      // Also map by id if different
      if (role.id && role.id !== role._id) {
        roleMap[role.id] = details;
      }
    });

    // Create a map of team ID to team details
    const teamMap = {};
    teams.forEach((team) => {
      const details = {
        teamName: team.team_name,
        objective: team.objective,
      };
      if (team._id) {
        teamMap[team._id] = details;
      }
      if (team.id && team.id !== team._id) {
        teamMap[team.id] = details;
      }
    });

    // Create a map of group ID to group details (for child_group lookup)
    const groupMap = {};
    allGroups.forEach((grp) => {
      const details = {
        groupName: grp.group_name,
        objective: grp.objective,
      };
      if (grp._id) {
        groupMap[grp._id] = details;
      }
      if (grp.id && grp.id !== grp._id) {
        groupMap[grp.id] = details;
      }
    });

    const membersMap = {};
    const teamsMap = {};
    const childGroupsMap = {};

    groups.forEach((group) => {
      // Use id or _id, whichever is available
      const groupId = group.id || group._id;
      membersMap[groupId] = [];
      teamsMap[groupId] = [];
      childGroupsMap[groupId] = [];

      // Process group_role (members)
      if (group.group_role && Array.isArray(group.group_role)) {
        group.group_role.forEach((roleEntry) => {
          const contributorId = roleEntry.contributor;
          const roleId = roleEntry._id || roleEntry.id;

          const contributorDetails = contributorMap[contributorId] || {
            fullName: "Unknown Contributor",
            email: "",
          };
          const roleDetails = roleMap[roleId] || {
            name: "Unknown Role",
            description: "",
          };

          membersMap[groupId].push({
            contributorId,
            contributorName: contributorDetails.fullName,
            contributorEmail: contributorDetails.email,
            roleId,
            roleName: roleDetails.name,
            roleDescription: roleDetails.description,
          });
        });
      }

      // Process team (array of team references)
      if (group.team && Array.isArray(group.team)) {
        group.team.forEach((teamId) => {
          const teamDetails = teamMap[teamId] || {
            teamName: "Unknown Team",
            objective: "",
          };
          teamsMap[groupId].push({
            teamId,
            teamName: teamDetails.teamName,
            objective: teamDetails.objective,
          });
        });
      }

      // Process child_group (array of group references)
      if (group.child_group && Array.isArray(group.child_group)) {
        group.child_group.forEach((childGroupId) => {
          const childGroupDetails = groupMap[childGroupId] || {
            groupName: "Unknown Group",
            objective: "",
          };
          childGroupsMap[groupId].push({
            groupId: childGroupId,
            groupName: childGroupDetails.groupName,
            objective: childGroupDetails.objective,
          });
        });
      }
    });

    return { membersMap, teamsMap, childGroupsMap };
  } catch (error) {
    return { membersMap: {}, teamsMap: {}, childGroupsMap: {} };
  }
};

// Get all group members for all groups at once (more efficient than individual calls)
// Kept for backwards compatibility
const getAllGroupMembers = async (groups) => {
  const { membersMap } = await getAllGroupData(groups);
  return membersMap;
};

// Batch calculate member counts for all groups
const getAllGroupMemberCounts = async () => {
  try {
    const groupsResponse = await api.get("/state_set/data", {
      params: {
        stateSetId: "Group",
        page: 0,
        pageSize: 99999,
      },
    });
    const groups = groupsResponse.data.list || [];

    const counts = {};

    groups.forEach((group) => {
      const groupId = group._id;
      const memberCount =
        group.group_role && Array.isArray(group.group_role)
          ? group.group_role.length
          : 0;
      counts[groupId] = memberCount;
    });

    return counts;
  } catch (error) {
    return {};
  }
};

// Clear all caches
const clearGroupCache = () => {
  contributorsCache = null;
  contributorsCacheTime = 0;
  groupRolesCache = null;
  groupRolesCacheTime = 0;
  teamsCache = null;
  teamsCacheTime = 0;
  allGroupsCache = null;
  allGroupsCacheTime = 0;
};

// Get hierarchy data for graph visualization
// Returns all groups with their child_group and team relationships resolved
const getGroupHierarchyData = async () => {
  // Clear cache to ensure fresh data
  clearGroupCache();

  const { allGroups, teams } = await fetchGroupRelatedData();

  // Create maps for lookups (handle both _id and id fields)
  const groupMap = {};
  allGroups.forEach((grp) => {
    const gId = grp._id || grp.id;
    if (gId) groupMap[gId] = grp;
  });

  const teamMap = {};
  teams.forEach((team) => {
    const tId = team._id || team.id;
    if (tId) teamMap[tId] = team;
  });

  // Build hierarchy data with resolved relationships
  const hierarchyData = allGroups.map((group) => {
    const childGroups = (group.child_group || []).map((childId) => {
      const child = groupMap[childId];
      const childIdResolved = child?._id || child?.id || childId;
      return child
        ? { _id: childIdResolved, group_name: child.group_name }
        : { _id: childId, group_name: "Unknown" };
    });

    const groupTeams = (group.team || []).map((teamId) => {
      const team = teamMap[teamId];
      const teamIdResolved = team?._id || team?.id || teamId;
      return team
        ? { _id: teamIdResolved, team_name: team.team_name, objective: team.objective }
        : { _id: teamId, team_name: "Unknown", objective: "" };
    });

    return {
      ...group,
      child_groups_resolved: childGroups,
      teams_resolved: groupTeams,
    };
  });

  return { list: hierarchyData, total: hierarchyData.length };
};

const GroupService = {
  getGroups,
  getRootGroups,
  getGroup,
  getGroupUi,
  createGroup,
  updateGroup,
  deleteGroup,
  getGroupMembers,
  getAllGroupMembers,
  getAllGroupData,
  getAllGroupMemberCounts,
  getGroupHierarchyData,
  clearGroupCache,
};

export default GroupService;
